import { useEffect, useRef } from "react";

/**
 * Adds an `is-visible` class when the element scrolls into view.
 * Also handles cases where the element is already in or near the viewport
 * on mount (e.g. after instant scroll), so content is never stuck invisible.
 * Honors prefers-reduced-motion automatically via CSS.
 */
export const useReveal = (options = {}) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const reveal = () => el.classList.add("is-visible");

    if (typeof IntersectionObserver === "undefined") {
      reveal();
      return;
    }

    // If already on screen at mount, reveal immediately on next frame.
    const checkInitial = () => {
      const rect = el.getBoundingClientRect();
      const inView =
        rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
      if (inView) reveal();
    };
    requestAnimationFrame(checkInitial);

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            reveal();
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.05, rootMargin: "0px 0px -10% 0px", ...options }
    );
    obs.observe(el);

    // Safety: ensure reveal after a short delay even if observer misses.
    const safety = setTimeout(reveal, 1200);

    return () => {
      obs.disconnect();
      clearTimeout(safety);
    };
  }, [options]);

  return ref;
};
